import { useState, useEffect, useRef } from 'react';

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate: {
        TranslateElement: new (options: Record<string, unknown>, elementId: string) => void;
      };
    };
  }
}

const LANGUAGES = [
  { code: 'es', label: 'Español', flag: '🇩🇴' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'zh-CN', label: '中文', flag: '🇨🇳' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
];

export default function TranslateButton() {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [activeLang, setActiveLang] = useState('es');
  const initialized = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    window.googleTranslateElementInit = () => {
      if (window.google?.translate) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'es',
            includedLanguages: LANGUAGES.map((l) => l.code).join(','),
            autoDisplay: false,
          },
          'g-translate-element'
        );
        setTimeout(() => setReady(true), 900);
      }
    };

    const script = document.createElement('script');
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLanguage = (code: string) => {
    const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
    if (!combo) return;
    combo.value = code;
    combo.dispatchEvent(new Event('change'));
    setActiveLang(code);
    setOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === activeLang) ?? LANGUAGES[0];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Hidden Google Translate element */}
      <div id="g-translate-element" className="hidden" />

      {/* Trigger button */}
      <button
        onClick={() => ready && setOpen(!open)}
        disabled={!ready}
        title="Cambiar idioma / Change language"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all whitespace-nowrap
          ${ready
            ? 'border-white/20 text-white/80 hover:bg-white/10 hover:text-white hover:border-white/40 cursor-pointer'
            : 'border-white/10 text-white/25 cursor-not-allowed'
          }
          ${open ? 'bg-white/12 border-white/40 text-white' : ''}`}
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-translate-2 text-sm" />
        </div>
        <span>{currentLang.flag} {currentLang.code === 'es' ? 'ES' : currentLang.code.toUpperCase().slice(0, 2)}</span>
        <div className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown — positioned to stay inside viewport */}
      {open && ready && (
        <div
          className="absolute right-0 top-full mt-2 w-48 bg-[#0B1F3A] border border-white/15 rounded-xl overflow-hidden z-50"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
        >
          <div
            className="px-3 py-2 border-b border-white/10"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <p className="text-white/40 text-[10px] font-semibold tracking-widest uppercase">
              Seleccionar idioma
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => selectLanguage(lang.code)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors cursor-pointer whitespace-nowrap
                  ${activeLang === lang.code
                    ? 'bg-white/12 text-white'
                    : 'text-white/65 hover:bg-white/8 hover:text-white'
                  }`}
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="font-medium">{lang.label}</span>
                {activeLang === lang.code && (
                  <div className="ml-auto w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-xs text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}